import { useEffect, useState } from 'react';

import { Box, Divider, Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { SxProps, Theme } from '@mui/material/styles';

import { useLanguage } from '../../../hooks';
import { InfoAlert } from '../../uiComponents/infoAlert';
import { MIN_OFFSET_ALLOWED_HOURS } from './constants';
import { CustomRangeConditionFields } from './customRangeConditionFields';
import {
  DateQuestionField,
  DateQuestionModeType,
  DateValidationType,
  RangeConditionType,
} from './types';
import { hasDateValidationErrors } from './utils';

const customStyles: Record<string, SxProps<Theme>> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '8px',
    marginBottom: '8px',
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '14px',
    marginBottom: '8px',
  },
  select: {
    '& .MuiSelect-select': {
      fontSize: '14px',
    },
  },
  placeholderText: {
    fontFamily: 'Noto Sans',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '14px',
    lineHeight: '22px',
    letterSpacing: '0px',
    verticalAlign: 'middle',
    color: (theme) => theme.palette.grey[600],
  },
  rangeConditionGroupLabel: {
    fontFamily: 'Noto Sans',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '12px',
    lineHeight: '20px',
    letterSpacing: '-0.02em',
    color: (theme) => theme.palette.grey[600],
    paddingLeft: '16px',
    paddingRight: '16px',
    paddingTop: '8px',
    paddingBottom: '8px',
  },
  validationTypeOption: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  validationTypeTitle: {
    fontFamily: 'Noto Sans',
    fontWeight: 500,
    fontStyle: 'normal',
    fontSize: '14px',
    lineHeight: '22px',
    letterSpacing: '0px',
    color: (theme) => theme.palette.secondary.main,
  },
  validationTypeDescription: {
    fontFamily: 'Noto Sans',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '14px',
    lineHeight: '22px',
    letterSpacing: '0px',
    color: (theme) => theme.palette.grey[600],
    marginTop: '4px',
  },
  divider: {
    margin: '0px',
  },
};

interface DateValidationProps {
  questionId: string;
  dateMode: DateQuestionModeType;
  dateValidationType?: DateValidationType | '';
  rangeCondition?: RangeConditionType;
  offsetHours?: string;
  offsetHoursToLeft?: string;
  offsetHoursToRight?: string;
  onDateValidationChange: (
    questionId: string,
    dateQuestionFieldToUpdate: DateQuestionField,
    value: string,
    tableId?: string,
  ) => void;
  onDateValidationBatchChange?: (
    questionId: string,
    dateQuestionFieldsToUpdate: Partial<Record<DateQuestionField, string>>,
    tableId?: string,
  ) => void;
  onDateValidationTableBatchChange?: (
    questionIdInTable: string,
    dateQuestionFieldsToUpdate: Partial<Record<DateQuestionField, string>>,
    tableId: string,
  ) => void;
  tableId?: string;
}

export const DateValidation = (props: DateValidationProps) => {
  const {
    questionId,
    dateMode,
    dateValidationType,
    rangeCondition,
    offsetHours,
    offsetHoursToLeft,
    offsetHoursToRight,
    onDateValidationChange,
    onDateValidationBatchChange,
    onDateValidationTableBatchChange,
    tableId,
  } = props;

  const [typeOfValidation, setTypeOfValidation] = useState<DateValidationType | '' | undefined>(
    dateValidationType === undefined ? undefined : dateValidationType,
  );
  const [selectedRangeCondition, setSelectedRangeCondition] = useState<RangeConditionType>(
    rangeCondition ?? undefined,
  );

  useEffect(() => {
    setTypeOfValidation(dateValidationType === undefined ? undefined : dateValidationType);
  }, [dateValidationType]);

  useEffect(() => {
    setSelectedRangeCondition(rangeCondition ?? undefined);
  }, [rangeCondition]);

  const lang = useLanguage();

  const safeDateMode: DateQuestionModeType = dateMode || 'datetime';

  const validationTypes: {
    value: DateValidationType;
    label: string;
    description: string;
    disabled?: boolean;
  }[] = [
    {
      value: 'fixedRange',
      label: lang.form_builder.dateValidation.fixedRange,
      description:
        safeDateMode === 'time'
          ? lang.form_builder.dateValidation.fixedRangeDescriptionTime
          : lang.form_builder.dateValidation.fixedRangeDescription,
    },
    {
      value: 'relativeRange',
      label: lang.form_builder.dateValidation.relativeRange,
      description:
        safeDateMode === 'time'
          ? lang.form_builder.dateValidation.relativeRangeDescriptionTime
          : lang.form_builder.dateValidation.relativeRangeDescription,
      disabled: true,
    },
  ];

  const hasValidation = dateValidationType !== undefined;

  const predefinedConditions =
    safeDateMode === 'time'
      ? [
          { value: 'onlyToday', label: lang.form_builder.dateValidation.onlyCurrentTime },
          { value: 'noFutureDates', label: lang.form_builder.dateValidation.noFutureTimes },
          { value: 'noPastDates', label: lang.form_builder.dateValidation.noPastTimes },
        ]
      : [
          { value: 'onlyToday', label: lang.form_builder.dateValidation.onlyToday },
          { value: 'noFutureDates', label: lang.form_builder.dateValidation.noFutureDates },
          { value: 'noPastDates', label: lang.form_builder.dateValidation.noPastDates },
        ];

  const customConditions = [
    { value: 'after', label: lang.form_builder.dateValidation.after },
    { value: 'afterOrEqual', label: lang.form_builder.dateValidation.afterOrEqual },
    { value: 'before', label: lang.form_builder.dateValidation.before },
    { value: 'beforeOrEqual', label: lang.form_builder.dateValidation.beforeOrEqual },
    { value: 'between', label: lang.form_builder.dateValidation.between },
    { value: 'notBetween', label: lang.form_builder.dateValidation.notBetween },
  ];

  const defaultOffsetInHours = MIN_OFFSET_ALLOWED_HOURS[safeDateMode].toString();

  /**
   * Builds the default offset fields object based on the range condition type.
   * This helper function centralizes the logic for configuring offset fields
   * according to the selected range condition.
   */
  const buildDefaultOffsetFields = (
    condition: RangeConditionType,
  ): Partial<Record<DateQuestionField, string>> => {
    const fields: Partial<Record<DateQuestionField, string>> = {
      rangeCondition: condition,
    };

    if (!condition) {
      return fields;
    }

    const isPredefinedRangeCondition = ['onlyToday', 'noFutureDates', 'noPastDates'].includes(
      condition,
    );
    const isBetweenTypeRangeCondition = ['between', 'notBetween'].includes(condition);

    if (isPredefinedRangeCondition) {
      fields.offsetHours = '';
      fields.offsetHoursToLeft = '';
      fields.offsetHoursToRight = '';
    } else if (isBetweenTypeRangeCondition) {
      fields.offsetHours = '';
      fields.offsetHoursToLeft = defaultOffsetInHours;
      fields.offsetHoursToRight = defaultOffsetInHours;
    } else {
      fields.offsetHours = defaultOffsetInHours;
      fields.offsetHoursToLeft = '';
      fields.offsetHoursToRight = '';
    }

    return fields;
  };

  const convertHoursToDaysAndHours = (hours: string) => {
    const totalHours = Number(hours);
    const days = Math.floor(totalHours / 24);
    const remainingHours = totalHours % 24;

    const daysUnitText =
      days === 1
        ? lang.form_builder.dateValidation.day.toLowerCase()
        : lang.form_builder.dateValidation.days.toLowerCase();
    const hoursUnitText =
      remainingHours === 1
        ? lang.form_builder.dateValidation.hour.toLowerCase()
        : lang.form_builder.dateValidation.hours.toLowerCase();

    return {
      days,
      hours: remainingHours,
      daysUnitText,
      hoursUnitText,
    };
  };

  const formatTimeText = (
    days: number,
    daysUnit: string,
    hours: number,
    hoursUnit: string,
  ): string => {
    if (days > 0 && hours > 0) {
      return `${days} ${daysUnit} ${lang.form_builder.dateValidation.and} ${hours} ${hoursUnit}`;
    } else if (days > 0) {
      return `${days} ${daysUnit}`;
    } else if (hours > 0) {
      return `${hours} ${hoursUnit}`;
    }
    return '';
  };

  const validationMessage = (() => {
    if (!selectedRangeCondition) return '';

    const validationMessages = lang.form_builder.dateValidation[safeDateMode];

    switch (selectedRangeCondition) {
      case 'after': {
        if (!offsetHours) return '';
        const { days, hours, daysUnitText, hoursUnitText } =
          convertHoursToDaysAndHours(offsetHours);
        if (safeDateMode === 'datetime') {
          const timeText = formatTimeText(days, daysUnitText, hours, hoursUnitText);
          const replacementPattern = `{days} {daysUnit} ${lang.form_builder.dateValidation.and} {hours} {hoursUnit}`;
          return validationMessages.afterMessage.replace(replacementPattern, timeText);
        }
        return validationMessages.afterMessage
          .replace('{days}', days.toString())
          .replace('{daysUnit}', daysUnitText)
          .replace('{hours}', hours.toString())
          .replace('{hoursUnit}', hoursUnitText);
      }

      case 'afterOrEqual': {
        if (!offsetHours) return '';
        const { days, hours, daysUnitText, hoursUnitText } =
          convertHoursToDaysAndHours(offsetHours);
        if (safeDateMode === 'datetime') {
          const timeText = formatTimeText(days, daysUnitText, hours, hoursUnitText);
          const replacementPattern = `{days} {daysUnit} ${lang.form_builder.dateValidation.and} {hours} {hoursUnit}`;
          return validationMessages.afterOrEqualMessage.replace(replacementPattern, timeText);
        }
        return validationMessages.afterOrEqualMessage
          .replace('{days}', days.toString())
          .replace('{daysUnit}', daysUnitText)
          .replace('{hours}', hours.toString())
          .replace('{hoursUnit}', hoursUnitText);
      }

      case 'before': {
        if (!offsetHours) return '';
        const { days, hours, daysUnitText, hoursUnitText } =
          convertHoursToDaysAndHours(offsetHours);
        if (safeDateMode === 'datetime') {
          const timeText = formatTimeText(days, daysUnitText, hours, hoursUnitText);
          const replacementPattern = `{days} {daysUnit} ${lang.form_builder.dateValidation.and} {hours} {hoursUnit}`;
          return validationMessages.beforeMessage.replace(replacementPattern, timeText);
        }
        return validationMessages.beforeMessage
          .replace('{days}', days.toString())
          .replace('{daysUnit}', daysUnitText)
          .replace('{hours}', hours.toString())
          .replace('{hoursUnit}', hoursUnitText);
      }

      case 'beforeOrEqual': {
        if (!offsetHours) return '';
        const { days, hours, daysUnitText, hoursUnitText } =
          convertHoursToDaysAndHours(offsetHours);
        if (safeDateMode === 'datetime') {
          const timeText = formatTimeText(days, daysUnitText, hours, hoursUnitText);
          const replacementPattern = `{days} {daysUnit} ${lang.form_builder.dateValidation.and} {hours} {hoursUnit}`;
          return validationMessages.beforeOrEqualMessage.replace(replacementPattern, timeText);
        }
        return validationMessages.beforeOrEqualMessage
          .replace('{days}', days.toString())
          .replace('{daysUnit}', daysUnitText)
          .replace('{hours}', hours.toString())
          .replace('{hoursUnit}', hoursUnitText);
      }

      case 'between': {
        if (!offsetHoursToLeft || !offsetHoursToRight) return '';
        const before = convertHoursToDaysAndHours(offsetHoursToLeft);
        const after = convertHoursToDaysAndHours(offsetHoursToRight);
        if (safeDateMode === 'datetime') {
          const timeTextBefore = formatTimeText(
            before.days,
            before.daysUnitText,
            before.hours,
            before.hoursUnitText,
          );
          const timeTextAfter = formatTimeText(
            after.days,
            after.daysUnitText,
            after.hours,
            after.hoursUnitText,
          );
          const replacementPatternBefore = `{daysBefore} {daysBeforeUnit} ${lang.form_builder.dateValidation.and} {hoursBefore} {hoursBeforeUnit}`;
          const replacementPatternAfter = `{daysAfter} {daysAfterUnit} ${lang.form_builder.dateValidation.and} {hoursAfter} {hoursAfterUnit}`;
          return validationMessages.betweenMessage
            .replace(replacementPatternBefore, timeTextBefore)
            .replace(replacementPatternAfter, timeTextAfter);
        }
        return validationMessages.betweenMessage
          .replace('{daysBefore}', before.days.toString())
          .replace('{daysBeforeUnit}', before.daysUnitText)
          .replace('{hoursBefore}', before.hours.toString())
          .replace('{hoursBeforeUnit}', before.hoursUnitText)
          .replace('{daysAfter}', after.days.toString())
          .replace('{daysAfterUnit}', after.daysUnitText)
          .replace('{hoursAfter}', after.hours.toString())
          .replace('{hoursAfterUnit}', after.hoursUnitText);
      }

      case 'notBetween': {
        if (!offsetHoursToLeft || !offsetHoursToRight) return '';
        const before = convertHoursToDaysAndHours(offsetHoursToLeft);
        const after = convertHoursToDaysAndHours(offsetHoursToRight);
        if (safeDateMode === 'datetime') {
          const timeTextBefore = formatTimeText(
            before.days,
            before.daysUnitText,
            before.hours,
            before.hoursUnitText,
          );
          const timeTextAfter = formatTimeText(
            after.days,
            after.daysUnitText,
            after.hours,
            after.hoursUnitText,
          );
          const replacementPatternBefore = `{daysBefore} {daysBeforeUnit} ${lang.form_builder.dateValidation.and} {hoursBefore} {hoursBeforeUnit}`;
          const replacementPatternAfter = `{daysAfter} {daysAfterUnit} ${lang.form_builder.dateValidation.and} {hoursAfter} {hoursAfterUnit}`;
          return validationMessages.notBetweenMessage
            .replace(replacementPatternBefore, timeTextBefore)
            .replace(replacementPatternAfter, timeTextAfter);
        }
        return validationMessages.notBetweenMessage
          .replace('{daysBefore}', before.days.toString())
          .replace('{daysBeforeUnit}', before.daysUnitText)
          .replace('{hoursBefore}', before.hours.toString())
          .replace('{hoursBeforeUnit}', before.hoursUnitText)
          .replace('{daysAfter}', after.days.toString())
          .replace('{daysAfterUnit}', after.daysUnitText)
          .replace('{hoursAfter}', after.hours.toString())
          .replace('{hoursAfterUnit}', after.hoursUnitText);
      }

      default:
        return '';
    }
  })();

  const hasValidationErrors = hasDateValidationErrors(
    selectedRangeCondition,
    safeDateMode,
    offsetHours,
    offsetHoursToLeft,
    offsetHoursToRight,
  );

  const handleTypeOfValidationChange = (value: DateValidationType) => {
    const oldTypeOfValidation = typeOfValidation;
    setTypeOfValidation(value);

    if (!value) return;

    if (oldTypeOfValidation !== value) {
      setSelectedRangeCondition(undefined);

      const fieldsToClear: Partial<Record<DateQuestionField, string>> = {
        rangeCondition: '',
        offsetHours: '',
        offsetHoursToLeft: '',
        offsetHoursToRight: '',
      };

      if (tableId) {
        onDateValidationTableBatchChange?.(questionId, fieldsToClear, tableId);
      } else {
        onDateValidationBatchChange?.(questionId, fieldsToClear);
      }
    }

    onDateValidationChange(questionId, 'dateValidationType', value, tableId);
  };

  const handleRangeConditionChange = (condition: RangeConditionType) => {
    setSelectedRangeCondition(condition);
    if (!condition) return;

    const dateQuestionFieldsToUpdate = buildDefaultOffsetFields(condition);

    if (tableId) {
      onDateValidationTableBatchChange?.(questionId, dateQuestionFieldsToUpdate, tableId);
    } else {
      onDateValidationBatchChange?.(questionId, dateQuestionFieldsToUpdate);
    }
  };

  const renderValidationTypeSelectedValue = (selectedValidationType: DateValidationType | '') => {
    if (!selectedValidationType) {
      return (
        <Typography sx={customStyles.placeholderText}>
          {lang.form_builder.dateValidation.selectRangeCondition}
        </Typography>
      );
    }
    const selectedType = validationTypes.find((type) => type.value === selectedValidationType);
    return selectedType?.label ?? '';
  };

  const renderRangeConditionSelectedValue = (
    selectedRangeConditionType: RangeConditionType | '',
  ) => {
    if (!selectedRangeConditionType) {
      return (
        <Typography sx={customStyles.placeholderText}>
          {lang.form_builder.dateValidation.selectRangeCondition}
        </Typography>
      );
    }
    const selectedOption = [...predefinedConditions, ...customConditions].find(
      (option) => option.value === selectedRangeConditionType,
    );
    return selectedOption?.label ?? '';
  };

  if (!hasValidation) {
    return null;
  }

  return (
    <Box sx={customStyles.container}>
      <Box sx={customStyles.fieldContainer}>
        <Typography sx={customStyles.label}>
          {lang.form_builder.dateValidation.validationType}
        </Typography>
        <Select
          value={typeOfValidation ?? ''}
          size='small'
          fullWidth
          sx={customStyles.select}
          onChange={(event) =>
            handleTypeOfValidationChange(event.target.value as DateValidationType)
          }
          displayEmpty
          renderValue={renderValidationTypeSelectedValue}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          }}
        >
          {validationTypes.map((type) => (
            <MenuItem
              key={type.value}
              value={type.value}
              disabled={type.disabled}
            >
              <Box sx={customStyles.validationTypeOption}>
                <Typography sx={customStyles.validationTypeTitle}>{type.label}</Typography>
                <Typography sx={customStyles.validationTypeDescription}>
                  {type.description}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </Box>

      <>
        <Box sx={customStyles.fieldContainer}>
          <Typography sx={customStyles.label}>
            {lang.form_builder.dateValidation.rangeCondition}
          </Typography>
          <Select
            value={selectedRangeCondition ?? ''}
            size='small'
            fullWidth
            sx={customStyles.select}
            onChange={(event) =>
              handleRangeConditionChange(event.target.value as RangeConditionType)
            }
            displayEmpty
            renderValue={renderRangeConditionSelectedValue}
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
          >
            <Typography sx={customStyles.rangeConditionGroupLabel}>
              {lang.form_builder.dateValidation.predefinedConditions}
            </Typography>
            {predefinedConditions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
              >
                {option.label}
              </MenuItem>
            ))}

            <Typography sx={customStyles.rangeConditionGroupLabel}>
              {lang.form_builder.dateValidation.customConditions}
            </Typography>
            {customConditions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {selectedRangeCondition && (
          <Box sx={customStyles.fieldContainer}>
            <CustomRangeConditionFields
              selectedRangeCondition={selectedRangeCondition}
              dateMode={dateMode}
              offsetHours={offsetHours}
              offsetHoursToLeft={offsetHoursToLeft}
              offsetHoursToRight={offsetHoursToRight}
              questionId={questionId}
              tableId={tableId}
              onDateValidationChange={onDateValidationChange}
            />
          </Box>
        )}

        {selectedRangeCondition &&
          !['onlyToday', 'noFutureDates', 'noPastDates'].includes(selectedRangeCondition) &&
          !hasValidationErrors && <InfoAlert message={validationMessage} />}
      </>
      <Divider sx={customStyles.divider} />
    </Box>
  );
};
